import { useParams, Link, Navigate } from "react-router-dom";
import { Section, Eyebrow, H2, Lead, GlassCard, Reveal } from "../components/site/Primitives";
import { findSub, SUBSIDIARIES } from "../data/subsidiaries";
import { ArrowRight, CheckCircle2 } from "lucide-react";

const GALLERY = [
  "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1200&q=80",
  "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1200&q=80",
  "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?w=1200&q=80",
];

export default function SubsidiaryDetail() {
  const { slug } = useParams();
  const sub = findSub(slug);
  if (!sub) return <Navigate to="/subsidiaries" replace />;

  // Veteran housing and club have their own dedicated pages — redirect
  if (sub.slug === "veteran-housing") return <Navigate to="/veteran-housing" replace />;
  if (sub.slug === "club-220") return <Navigate to="/club-220" replace />;

  return (
    <div>
      <section className="relative pt-40 pb-20 grain">
        <div className="absolute inset-0">
          <img src={sub.image} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-[#0A0A0A]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Eyebrow>{sub.tag}</Eyebrow>
          <h1 className="font-display text-5xl sm:text-7xl tracking-tighter font-medium">{sub.name}</h1>
          <p className="mt-6 text-lg text-white/70 max-w-3xl leading-relaxed">{sub.long}</p>
        </div>
      </section>

      <Section className="border-t border-white/5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Eyebrow>Services</Eyebrow>
            <H2>What we deliver.</H2>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {sub.services.map((s) => (
              <div key={s} className="flex items-center gap-3 border border-white/10 px-4 py-3 bg-[#0e0e10]">
                <CheckCircle2 size={16} className="text-gold flex-shrink-0" />
                <span className="text-sm text-white/80">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section className="border-t border-white/5">
        <Eyebrow>Project Gallery</Eyebrow>
        <H2 className="max-w-3xl">Selected works and active scopes.</H2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {GALLERY.map((g, i) => (
            <Reveal key={g} delay={i * 0.05}>
              <div className="aspect-[4/3] overflow-hidden border border-white/10">
                <img src={g} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="border-t border-white/5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <Eyebrow>Leadership</Eyebrow>
            <H2>Operators who've built before.</H2>
            <Lead className="mt-6">
              Each subsidiary is led by a senior operator paired with discipline leads. Leadership
              bios and headshots will be added at brand launch.
            </Lead>
          </div>
          <div className="lg:col-span-6">
            <Eyebrow>Future Goals</Eyebrow>
            <H2>What we're building next.</H2>
            <ul className="mt-6 space-y-3 text-white/70">
              <li>· Scale crews to support Phase 1 corridor volume</li>
              <li>· Vertically integrate trades through 220 Education pipeline</li>
              <li>· Launch contractor portal for live bidding and permit tracking</li>
              <li>· Expand regional footprint across the tri-state corridor</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section className="border-t border-white/5">
        <GlassCard className="p-10 md:p-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <Eyebrow>Contact {sub.name}</Eyebrow>
              <H2>Have a project, partnership, or career interest?</H2>
            </div>
            <div className="lg:col-span-4 flex flex-wrap gap-4 lg:justify-end">
              <Link to={`/contact?topic=${sub.slug}`} className="btn-primary" data-testid="sub-contact-cta">
                Contact <ArrowRight size={14} />
              </Link>
              <Link to="/careers" className="btn-ghost">Careers</Link>
            </div>
          </div>
        </GlassCard>
      </Section>

      <Section className="border-t border-white/5">
        <Eyebrow>Explore Other Divisions</Eyebrow>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {SUBSIDIARIES.filter((s) => s.slug !== sub.slug).map((s) => (
            <Link key={s.slug} to={s.href} className="border border-white/10 p-4 hover:border-gold transition-colors block">
              <div className="text-xs text-white/40 uppercase tracking-[0.2em]">Division</div>
              <div className="font-display text-sm mt-1">{s.name}</div>
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
}
